import { getQuarter, parsePredefinedCondition } from "./comboDateUtils";
import { ColumnConfigReportComboApi, ColumnConfigValueReportComboApi, CompareFormatReportComboApi } from "./types";

function getLast12Months(date: Date): ColumnConfigValueReportComboApi[] {
	const result: ColumnConfigValueReportComboApi[] = [];

	for (let i = 0; i < 12; i++) {
		const month = ((date.getMonth() + 1 - i + 11) % 12) + 1;
		result.push({ id: month.toString() });
	}
	return result;
}

function getLastXMonths(date: Date, number: number): ColumnConfigValueReportComboApi[] {
	const result: ColumnConfigValueReportComboApi[] = [];

	for (let i = 0; i < number; i++) {
		const month = ((date.getMonth() + 1 - i + 11) % 12) + 1;
		result.push({ id: month.toString() });
	}
	return result;
}

export class ReportComboUtils {
	static getDataColumnId = (configColumn: ColumnConfigReportComboApi) => {
		if (configColumn.data_column_id) return configColumn.data_column_id;
		return configColumn.id;
	};

	static renderValueObj = (value: any, format?: CompareFormatReportComboApi) => {
		if (format) {
			if (format === "PERCENT") {
				return `${value.toFixed(0)} %`;
			}
		}
		if (typeof value === "object" && value !== null && "amount" in value && "currency" in value) {
			return `${value.amount} ${value.currency}`;
		}
		if (typeof value === "number" && !isNaN(value)) {
			return Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, "");
		}
		return `${value}`;
	};

	static getValueFromValueObj = (value: any) => {
		if (!value) return 0;
		if (typeof value === "object" && value !== null && "amount" in value && "currency" in value) {
			return parseFloat(value.amount);
		}
		return parseFloat(value);
	};

	static parseValueToObject = (value: any, newValue: number) => {
		if (typeof value === "object" && value !== null && "amount" in value && "currency" in value) {
			return { ...value, amount: newValue };
		}
		return newValue;
	};

	static getDynamicValues = (dynamicValue: string) => {
		if (/^values_previous_(\d+)_months(¥.*)?$/.test(dynamicValue)) {
			const parts = dynamicValue.split("¥");
			const value = parts[0];
			const extractMonthValue = parseInt(value.match(/\d+/)?.[0] || "0", 10);
			const variable = parts[1];
			let date = new Date();
			if (variable) {
				const variableValue = parsePredefinedCondition(variable, new Date());
				if (variableValue) {
					const endDate = variableValue.split("¥")[1];
					if (endDate) {
						date = new Date(endDate);
					}
				}
			}

			return getLastXMonths(date, extractMonthValue);
		}

		let startDate;
		let endDate;
		let match = dynamicValue.match(/^previous_(\d+)_quarters$/);
		if (match) {
			const value = parseInt(match[1], 10);
			startDate = getQuarter(new Date(), value);
			endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0, 23, 59, 59);
			return getLastXMonths(endDate, 3);
		}

		return undefined;
	};

	static findDuplicates = (items: any[], key: string) => {
		const seenIds = new Set<string>();
		const duplicates: string[] = [];

		for (const row of items) {
			const value = row[key];
			if (seenIds.has(value)) {
				duplicates.push(value);
			} else {
				seenIds.add(row[key]);
			}
		}

		return duplicates;
	};

	static findVariableIdsInString = (jsonString: string) => {
		const regex = /\{(\w+)\}/g;
		const variables: string[] = [];
		let match;

		while ((match = regex.exec(jsonString)) !== null) {
			const value = match[1];
			if (!variables.includes(value)) {
				variables.push(value); // Capture the variable name without the curly braces
			}
		}

		return variables;
	};

	static findVariablesInString = (jsonString: string) => {
		const regex = /\{([\w_]+)(?:¥([^}]+))?\}/g;
		const variables: Record<string, string[]> = {};
		let match;

		while ((match = regex.exec(jsonString)) !== null) {
			let key = match[1];
			const option = match[2] !== undefined ? match[2] : "-";
			// let value = match[1];
			if (key.includes("¥")) {
				key = key.split("¥")[0];
			}
			if (!variables[key]) {
				variables[key] = [];
			}
			if (!variables[key].includes(option)) {
				variables[key].push(option);
			}
		}

		return variables;
	};
	static parseCustomFilter = (originalValue: string, customValue: string) => {
		let match = originalValue.match(/^previous_(\d+)_quarters$/);
		if (match) {
			const value = parseInt(match[1], 10);
			let newMatch = customValue.match(/^previous_(\d+)_quarters$/);
			if (newMatch) {
				const newValue = parseInt(newMatch[1], 10);
				return `previous_${value + newValue}_quarters`;
			}
			// newMatch = customValue.match(/^previous_(\d+)_months/);
			// newMatch = customValue.match(/^previous_(\d+)-(\d+)_months$/);
			// if (newMatch) {
			//     const newValue = parseInt(newMatch[1], 10);
			//     return `previous_${value + newValue}_months`;
			// }
			// match = predefinedRange.match(/^previous_(\d+)-(\d+)_months$/);
			// if (match) {
			//     const monthsAgoEnd = parseInt(match[1], 10); // Older range (e.g., 6 months ago)
			//     const monthsAgoStart = parseInt(match[2], 10); // More recent range (e.g., 3 months ago)
			//     startDate = new Date(date.getFullYear(), date.getMonth() - monthsAgoStart, 1, 0, 0, 0);
			//     endDate = new Date(date.getFullYear(), date.getMonth() - monthsAgoEnd + 1, 0, 23, 59, 59);
			//     break;
			// }
		}
		match = originalValue.match(/^previous_(\d+)-(\d+)_months$/);
		if (match) {
			const value = parseInt(match[1], 10);
			const value2 = parseInt(match[2], 10);
			const newMatch = customValue.match(/^previous_([\d\-]+)_months/);
			if (newMatch) {
				const newValue = parseInt(newMatch[1], 10);
				return `previous_${value + newValue}-${value2 + newValue}_months`;
			}
		}
		match = originalValue.match(/^previous_(\d+)_months$/);
		if (match) {
			const value = parseInt(match[1], 10);
			const newMatch = customValue.match(/^previous_(\d+)_months$/);
			if (newMatch) {
				const newValue = parseInt(newMatch[1], 10);
				return `previous_${value + 1}-${value + newValue}_months`;
			}
		}
	};
}
